import React from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Step,
    StepLabel,
    Stepper,
    Switch,
    TextField,
    Typography
} from '@mui/material';

type UnleashedIntegrationSettingsProps = {
    activeStep: number;
    steps: string[];
    apiId: string;
    apiKey: string;
    integrationEnabled: boolean;
    canEnableIntegration: boolean;
    savingSettings: boolean;
    loadingProductGroups: boolean;
    autoDisabledMessage: string | null;
    productGroups: string[];
    selectedProductGroups: string[];
    allProductsSelected: boolean;
    includeNonSellable: boolean;
    onApiIdChange: (value: string) => void;
    onApiKeyChange: (value: string) => void;
    onToggleEnabled: (checked: boolean) => void;
    onAllProductsChange: (checked: boolean) => void;
    onProductGroupsChange: (value: string[]) => void;
    onIncludeNonSellableChange: (checked: boolean) => void;
    onBack: () => void;
    onNext: () => void;
};

export default function UnleashedIntegrationSettings({
    activeStep,
    steps,
    apiId,
    apiKey,
    integrationEnabled,
    canEnableIntegration,
    savingSettings,
    loadingProductGroups,
    autoDisabledMessage,
    productGroups,
    selectedProductGroups,
    allProductsSelected,
    includeNonSellable,
    onApiIdChange,
    onApiKeyChange,
    onToggleEnabled,
    onAllProductsChange,
    onProductGroupsChange,
    onIncludeNonSellableChange,
    onBack,
    onNext
}: UnleashedIntegrationSettingsProps) {
    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <form style={{ width: '100%' }}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            label="API ID"
                            value={apiId}
                            onChange={(event) => onApiIdChange(event.target.value)}
                        />
                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            label="API Key"
                            value={apiKey}
                            onChange={(event) => onApiKeyChange(event.target.value)}
                        />
                    </form>
                );
            case 1:
                return (
                    <>
                        <Typography
                            variant="subtitle1"
                            sx={{ marginTop: '16px', marginBottom: '8px', textAlign: 'left', width: '100%' }}
                        >
                            Product groups to sync:
                        </Typography>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={allProductsSelected}
                                    onChange={(event) => onAllProductsChange(event.target.checked)}
                                    color="primary"
                                />
                            }
                            label="All Products"
                            style={{ marginBottom: '16px', width: '100%' }}
                        />

                        {!allProductsSelected && (
                            <FormControl
                                fullWidth
                                variant="outlined"
                                disabled={loadingProductGroups}
                                style={{ marginBottom: '16px' }}
                            >
                                <InputLabel id="product-groups-label">Product groups</InputLabel>
                                <Select
                                    labelId="product-groups-label"
                                    multiple
                                    value={selectedProductGroups}
                                    onChange={(event) => onProductGroupsChange(typeof event.target.value === 'string'
                                        ? event.target.value.split(',')
                                        : event.target.value)}
                                    input={<OutlinedInput label="Product Groups" />}
                                    renderValue={(selected) => selected.join(', ')}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 300
                                            }
                                        }
                                    }}
                                >
                                    {loadingProductGroups ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={24} />
                                            <Typography style={{ marginLeft: '10px' }}>Loading product groups...</Typography>
                                        </MenuItem>
                                    ) : (
                                        productGroups.map((group) => (
                                            <MenuItem key={group} value={group}>
                                                <Checkbox checked={selectedProductGroups.indexOf(group) > -1} />
                                                <ListItemText primary={group} />
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        )}

                        <Divider style={{ width: '100%', marginBottom: '16px' }} />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={includeNonSellable}
                                    onChange={(event) => onIncludeNonSellableChange(event.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Include non-sellable products"
                            style={{ width: '100%' }}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            {autoDisabledMessage && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography>Integration was automatically disabled:</Typography>
                    <Typography sx={{ fontWeight: 'bold', my: 1 }}>{autoDisabledMessage}</Typography>
                    <Typography>Enable the integration again after resolving the issue.</Typography>
                </Alert>
            )}

            {canEnableIntegration && (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 3 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={integrationEnabled}
                                onChange={(event) => onToggleEnabled(event.target.checked)}
                                color="primary"
                                disabled={savingSettings}
                            />
                        }
                        label="Enabled"
                    />
                    {savingSettings && (
                        <CircularProgress size={20} sx={{ ml: 1 }} />
                    )}
                </Box>
            )}

            <Typography variant="subtitle1" sx={{ width: '100%', mb: 2 }}>
                Unleashed configuration
            </Typography>

            <Divider style={{ width: '100%', marginBottom: '16px' }} />

            <Stepper activeStep={activeStep} style={{ width: '100%', marginBottom: '20px' }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {renderStepContent()}

            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                    color="inherit"
                    disabled={activeStep === 0}
                    onClick={onBack}
                    sx={{ mr: 1 }}
                >
                    Back
                </Button>
                <Box sx={{ flex: '1 1 auto' }} />
                {loadingProductGroups && (
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                )}
                <Button
                    onClick={onNext}
                    variant="contained"
                    color="primary"
                    disabled={
                        ((!apiId || !apiKey) && activeStep === 0) ||
                        (activeStep === 1 && !allProductsSelected && selectedProductGroups.length === 0) ||
                        loadingProductGroups ||
                        savingSettings
                    }
                >
                    {activeStep === steps.length - 1 ? 'Save' : 'Next'}
                </Button>
            </Box>
        </>
    );
}
